import styled from "styled-components";

const TileList = styled.ul`
  display: flex;
  flex-wrap: ${({ $wrap }) => ($wrap ? "wrap" : "nowrap")};
  gap: 8px;
  width: ${({ $fill }) => ($fill ? "100%" : "fit-content")};
  min-width: ${({ $fill }) => ($fill ? "max-content" : "0")};
  list-style: none;
  margin: 0;
  padding: 0;

  > li {
    flex: ${({ $fill }) => ($fill ? "1 1 0" : "0 0 auto")};
  }
`;

const tileStyles = `
  width: 100%;
  min-width: 70px;
  min-height: 86px;
  display: grid;
  place-items: center;
  padding: 9px 7px 8px;
  border-radius: 7px;
  color: var(--app-ink);
  font-family: "DM Sans", Arial, sans-serif;
  cursor: pointer;
  box-shadow: none;
  transform: none;
  transition: border-color 160ms ease, background-color 160ms ease,
    box-shadow 160ms ease, transform 160ms ease;
`;

const TileButton = styled.button`
  ${tileStyles}
  ${({ $layout }) =>
    $layout === "horizontal"
      ? `
        min-width: 0;
        min-height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        padding: 9px 12px;
      `
      : `grid-template-rows: auto 1fr auto;`}
  border: 2px solid
    ${({ $selected }) => ($selected ? "#aebd8b" : "var(--app-line)")};
  background: ${({ $selected }) =>
    $selected ? "#dfe7ce" : "var(--app-control)"};

  &:hover,
  &:focus-visible {
    border-color: ${({ $selected }) => ($selected ? "#aebd8b" : "#cbc4b6")};
    box-shadow: ${({ $selected }) =>
      $selected ? "none" : "0 4px 9px rgba(23, 25, 20, 0.12)"};
    transform: translateY(${({ $selected }) => ($selected ? "0" : "-1px")});
  }

  &:focus-visible {
    outline: 3px solid rgba(242, 100, 60, 0.4);
    outline-offset: 2px;
  }

  &:active {
    box-shadow: none;
    transform: none;
  }

  .date-weekday {
    font-family: "DM Mono", monospace;
    font-size: 0.63rem;
    font-weight: 500;
    text-transform: uppercase;
  }

  .date-number {
    align-self: center;
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1;
  }

  .date-meta {
    color: var(--app-muted);
    font-size: 0.68rem;
    font-weight: 400;
  }

  .selection-tile__icon {
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
  }

  .selection-tile__label {
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.2;
  }
`;

const TileLabel = styled.label`
  ${tileStyles}
  min-width: 92px;
  grid-template-rows: 1fr auto;
  gap: 6px;
  position: relative;
  border: 2px solid
    ${({ $invalid, $selected }) =>
      $invalid ? "#b42318" : $selected ? "#aebd8b" : "var(--app-line)"};
  background: ${({ $selected }) =>
    $selected ? "#dfe7ce" : "var(--app-control)"};

  &:hover,
  &:focus-within {
    border-color: ${({ $invalid, $selected }) =>
      $invalid ? "#b42318" : $selected ? "#aebd8b" : "#cbc4b6"};
    box-shadow: ${({ $selected }) =>
      $selected ? "none" : "0 4px 9px rgba(23, 25, 20, 0.12)"};
    transform: translateY(${({ $selected }) => ($selected ? "0" : "-1px")});
  }

  &:has(input:focus-visible) {
    outline: 3px solid rgba(242, 100, 60, 0.4);
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  strong {
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.15;
  }
`;

function SelectionTiles({
  ariaLabel,
  fill = false,
  getInputProps,
  invalid = false,
  layout = "default",
  multiple = false,
  onChange,
  options,
  renderOption,
  type = "button",
  value,
  wrap = false,
}) {
  const isRadio = type === "radio";

  return (
    <TileList
      $fill={fill}
      $wrap={wrap}
      aria-label={ariaLabel}
      role={isRadio ? "radiogroup" : undefined}
    >
      {options.map((option) => {
        const selected = multiple
          ? Array.isArray(value) && value.includes(option.value)
          : option.value === value;

        return (
          <li key={option.value}>
            {isRadio ? (
              <TileLabel $selected={selected} $invalid={invalid}>
                <input
                  type="radio"
                  value={option.value}
                  {...getInputProps(option)}
                />
                {renderOption(option, selected)}
              </TileLabel>
            ) : (
              <TileButton
                type="button"
                $selected={selected}
                $layout={layout}
                aria-pressed={selected}
                aria-label={option.ariaLabel}
                onClick={() => onChange(option.value)}
              >
                {renderOption(option, selected)}
              </TileButton>
            )}
          </li>
        );
      })}
    </TileList>
  );
}

export default SelectionTiles;
